/**
 * ActionLog — scrolling feed of last N accepted actions.
 *
 * Originally derived from the (removed) board-game-playground for visual parity. UI is NOT shared (CLAUDE.md).
 */

import type { Action } from "@cloudnest/cascadia-engine";

export interface ActionLogProps {
  readonly history: readonly Action[];
  readonly maxEntries?: number;
}

const MAX_DEFAULT = 20;

function actionSummary(action: Action): string {
  switch (action.type) {
    case "Init":
      return "Init game";
    case "DraftPair":
      return `DraftPair slot ${action.slot}`;
    case "DraftDecoupled":
      return `DraftDecoupled tile:${action.tileSlot} wildlife:${action.wildlifeSlot}`;
    case "SpendNatureDecouple":
      return "SpendNatureDecouple";
    case "SpendNatureWipe":
      return `SpendNatureWipe [${action.slots.join(",")}]`;
    case "WipeMarketOptional":
      return "WipeMarketOptional";
    case "PlaceTile":
      return `PlaceTile (${action.coord.q},${action.coord.r})`;
    case "PlaceWildlife":
      return `PlaceWildlife (${action.coord.q},${action.coord.r})`;
    case "ReturnWildlife":
      return "ReturnWildlife";
    case "UnlockLandmark":
      return `UnlockLandmark ${action.habitat}`;
    case "ChooseLandmarkCard":
      return `ChooseLandmarkCard ${action.cardId}`;
    case "SkipLandmark":
      return "SkipLandmark";
    case "EndTurn":
      return "EndTurn";
    default: {
      const _exhaustive: never = action;
      return String((_exhaustive as Action).type);
    }
  }
}

export function ActionLog({
  history,
  maxEntries = MAX_DEFAULT,
}: ActionLogProps) {
  const entries = [...history].reverse().slice(0, maxEntries);

  return (
    <section
      data-testid="cascadia-action-log"
      aria-label="Action log"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
        background: "var(--cn-cascadia-tile-bg)",
        border: "1px solid var(--cn-cascadia-market-slot-border)",
        borderRadius: 8,
        overflow: "hidden",
        maxHeight: 240,
      }}
    >
      <div
        style={{
          padding: "6px 10px",
          fontSize: 11,
          fontWeight: 600,
          color: "var(--cn-cascadia-label)",
          background: "var(--cn-cascadia-market-slot)",
          borderBottom: "1px solid var(--cn-cascadia-tray-empty-slot)",
        }}
      >
        Action Log
      </div>
      <div
        style={{
          overflowY: "auto",
          flex: 1,
        }}
      >
        {entries.length === 0 ? (
          <div
            style={{
              padding: "8px 10px",
              fontSize: 11,
              color: "var(--cn-cascadia-tray-empty-slot)",
              fontStyle: "italic",
            }}
          >
            No actions yet
          </div>
        ) : (
          entries.map((action, i) => (
            <div
              key={`${i}-${action.type}`}
              data-testid={`cascadia-action-log-entry-${entries.length - 1 - i}`}
              style={{
                padding: "4px 10px",
                fontSize: 11,
                color: "var(--cn-cascadia-label)",
                borderBottom: "1px solid var(--cn-cascadia-tray-empty-slot)",
              }}
            >
              {actionSummary(action)}
            </div>
          ))
        )}
      </div>
    </section>
  );
}
